import store from "@/store"
import { IComponentTemplate, IDefaultRecipe } from "@/store/modules/recipe/recipe.t"
import {
    GraphicComponent,
    GraphicComponentTypes,
    IGraphic,
    IViewBox,
    PlacementSetting,
    PlacementTypes
} from "./types/Graphics.t"
import { getUGCDataByID } from "./UGCData"
import { getUGCViewBoxAsCover } from "./UGCHandler"
import { fetchAndInsertImageToGraphicSettings } from "@/libs/graphics/GraphicImageAssetProcesser"
const cloneDeep = require("lodash/cloneDeep")

/**
 * This function takes in a recipe and a component ID and returns a formatted component template
 * @param {PlacementTypes} componentID - PlacementTypes
 * @param {GraphicComponentTypes} subComponentID - The subcomponent that we're formatting.
 * @param {IDefaultRecipe} recipe - IDefaultRecipe
 * @returns A component template that has been formatted to be used in the SVG.
 */

export interface FormatRecipeForSVGServiceProps {
    componentID: PlacementTypes,
    subComponentID?: GraphicComponentTypes
    recipe?: IDefaultRecipe
    recipeComponent?: IComponentTemplate
    graphicInput?: IGraphic
    hrefOverride?: string
    restrict?: boolean
}

const FormatRecipeForSVGService = async (
    props: FormatRecipeForSVGServiceProps
): Promise<IComponentTemplate | undefined> => {

    const formattedRecipeComponentData: IComponentTemplate = props.recipeComponent ?? cloneDeep(
        props.recipe?.components?.[props.componentID]
    )

    if (!formattedRecipeComponentData) return
    const graphic: IGraphic = props.graphicInput ?? await store.dispatch("gdm/GET_GDM", {
        id: formattedRecipeComponentData.swatch_id,
        component3D: props.componentID,
    })

    if (!graphic || graphic.id !== formattedRecipeComponentData.swatch_id) return

    for (const component_id in formattedRecipeComponentData.sub_components) {
        if (props.restrict && !(props.subComponentID === component_id)) continue;

        const subcomponent = formattedRecipeComponentData.sub_components[
            component_id as GraphicComponentTypes
        ] as IComponentTemplate

        if (subcomponent.group === "patterns") {
            const componentSetting: PlacementSetting =
                (store.state as any).graphicsController.placementSetting.find(
                    (item: PlacementSetting) =>
                        item.placementComponent === props.componentID
                )

            const graphicComponent: GraphicComponent | undefined =
                componentSetting?.componentSetting?.find(
                    (item: GraphicComponent) => item.id === component_id
                ) as GraphicComponent

            // add the pattern data to the formatted recipe component
            if (graphicComponent?.pattern) {
                formattedRecipeComponentData.sub_components![
                    component_id as GraphicComponentTypes
                ]!.pattern_data = graphicComponent.pattern
            } else {
                // pattern data is not available in the store, so we will fetch an insert into the formatted graphics recipe.
                const patternSwatchData = store.getters['swatches/returnSwatchObj']({ swatchId: subcomponent.swatch_id });
                await fetchAndInsertImageToGraphicSettings({
                    src: `${(store.state as any).endPoints.customizer.s3SwatchesBucket
                        }assets${patternSwatchData?.image_assets?.svg_map ??
                        patternSwatchData?.material_options?.textures?.map.image_url}`,
                    componentID: props.componentID,
                    subcomponentID: component_id as GraphicComponentTypes,
                    swatch_id: subcomponent.swatch_id,
                });
                formattedRecipeComponentData.sub_components![
                    component_id as GraphicComponentTypes
                ]!.pattern_data = (store.state as any).graphicsController.placementSetting.find(
                    (item: PlacementSetting) =>
                        item.placementComponent === props.componentID
                )?.componentSetting?.find(
                    (item: GraphicComponent) => item.id === component_id
                ).pattern
            }
        }

        if (subcomponent.group === "ugc") {
            const ugcDataStore = getUGCDataByID(
                props.componentID,
                component_id as GraphicComponentTypes
            )
            formattedRecipeComponentData.sub_components![
                component_id as GraphicComponentTypes
            ]!.ugc_data = ugcDataStore?.ugcData

            const existingGraphicUGCSetting = store.getters[
                "graphicsController/getGraphicUGCSetting"
            ](props.componentID, graphic.id)?.viewBox

            const targetComponentViewbox = store.getters[
                "gdm/getComponentUgcData"
            ](props.componentID, component_id ?? "fill_ugc")?.viewbox

            const initialUgcViewBox: IViewBox = {
                x: 0,
                y: 0,
                width: ugcDataStore?.ugcData.width ?? 1,
                height: ugcDataStore?.ugcData.height ?? 1,
            }

            let ugcViewBox: IViewBox =
                // use the viewbox from the store if it exists
                existingGraphicUGCSetting ||
                // or generate a the viewbox from our image size and target size.
                getUGCViewBoxAsCover(targetComponentViewbox, initialUgcViewBox)

            store.dispatch("graphicsController/ADD_GRAPHIC_UGC_ITEM", {
                placement: props.componentID,
                graphicID: graphic.id,
                viewBox: ugcViewBox,
            })

            formattedRecipeComponentData.sub_components![
                component_id as GraphicComponentTypes
            ]!.toolkit_options = {
                viewBox: ugcViewBox,
            }

            store.commit("recipe/SET_SUBCOMPONENT_TOOLKIT", {
                componentID: props.componentID,
                subComponentID: component_id,
                payload: {
                    viewBox: ugcViewBox,
                    zoomValue: ugcDataStore?.zoomValue
                },
            })
        }
    }

    return formattedRecipeComponentData
}

export default FormatRecipeForSVGService
